import React, { useEffect, useRef, useCallback } from "react";
import type { Variants } from "framer-motion";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "../../utils/cn";
import { fleetrifyIcons } from "../../assets/icons/icons";
import { createPortal } from "react-dom";

interface ModalProps {
  isOpen: boolean;
  onClose?: () => void;
  title?: string;
  children: React.ReactNode;
  className?: string;
  titleClassName?: string;
  contentClassName?: string;
  inputClassName?: string;
  overlayClassName?: string;
  disableClose?: boolean;
  enableOutsideClick?: boolean;
  childrenClassName?: string
}
export const FleetrifyModal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  className,
  titleClassName,
  contentClassName,
  inputClassName,
  overlayClassName,
  disableClose = false,
  enableOutsideClick = true,
  childrenClassName,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  const handleOutsideClick = useCallback(
    (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node) &&
        !disableClose &&
        enableOutsideClick
      ) {
        onClose?.();
      }
    },
    [onClose, disableClose, enableOutsideClick]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
      document.body.style.overflow = "hidden";
    } else {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.body.style.overflow = "unset";
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, handleOutsideClick]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen && !disableClose) {
        onClose?.();
      }
    };

    if (isOpen) {
      window.addEventListener("keydown", handleKeyDown);
    } else {
      window.removeEventListener("keydown", handleKeyDown);
    }

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose, disableClose]);

  // const modalVariants = {
  //   hidden: { opacity: 0, scale: 0.8 },
  //   visible: {
  //     opacity: 1,
  //     scale: 1,
  //     transition: { type: "spring", stiffness: 260, damping: 20 },
  //   },
  //   exit: { opacity: 0, scale: 0.7, transition: { duration: 0.2 } },
  // };
  const modalVariants: Variants = {
    hidden: { x: "100%" },
    visible: {
      x: 0,
      transition: { type: "spring" as const, stiffness: 300, damping: 30 },
    },
    exit: {
      x: "100%",
      transition: { type: "tween" as const, duration: 1.2, ease: "easeInOut" },
    },
  };

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className={cn(
            "fixed inset-0 z-50 px-3 flex items-center justify-center bg-black/50 backdrop-blur-lg",
            overlayClassName
          )}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <motion.div
            ref={modalRef}
            className={cn(
              "relative w-full max-h-[90vh] max-w-md overflow-hidden hide-scrollbar bg-white rounded-xl shadow-2xl flex flex-col",
              className
            )}
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            role="dialog"
            aria-modal="true"
            aria-labelledby={title ? "modal-title" : undefined}
          >
            <div className="flex items-center gap-2 px-4 sm:px-6 py-2 bg-[#14388733] min-h-12 justify-between flex-shrink-0">
              <h2
                className={cn(
                  "text-[13px] font-bold text-black min-w-0 truncate",
                  titleClassName
                )}
              >
                {title}
              </h2>
              {!disableClose && (
                <button
                  onClick={onClose}
                  className="bg-white rounded-[4px] h-[24px] w-[24px] flex-shrink-0 grid place-items-center"
                  aria-label="Close modal"
                >
                  <figure>{fleetrifyIcons.cancelIcon}</figure>
                </button>
              )}
            </div>
            <div className={cn("flex-1 space-y-4 p-6 pb-10 overflow-y-auto hide-scrollbar", childrenClassName)}>
              {children}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};
